import axios from "axios";
import { toast } from "react-toastify";

const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_ADMIN_API,
  withCredentials: true,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Helper function to get cookie value by name
const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

export const fetchCsrfToken = async () => {
  try {
    const { data } = await api.get('/csrf-token');
    if (data?.csrfToken) {
      api.defaults.headers.common['X-CSRF-Token'] = data.csrfToken;
    }
  } catch (error) {
    console.error("Failed to fetch CSRF token:", error);
  }
};

// Immediately fetch CSRF token on module import
fetchCsrfToken();

// Add Authorization header with token from cookie or localStorage
api.interceptors.request.use(
  (config) => {
    // Try several places where the token might be stored (cookie or various localStorage keys)
    let token = null;

    // Cookies (common names)
    token = getCookie('access_token') || getCookie('accessToken') || getCookie('token');

    // Local storage (check multiple possible keys and parsed objects)
    if (!token && typeof localStorage !== 'undefined') {
      const candidates = ['token', 'access_token', 'accessToken', 'authToken'];
      candidates.some((key) => {
        const stored = localStorage.getItem(key);
        if (!stored) return false;
        try {
          const parsed = JSON.parse(stored);
          // parsed might be the raw token or an object containing token/access_token
          if (typeof parsed === 'string') {
            token = parsed;
            return true;
          }
          if (parsed && typeof parsed === 'object') {
            token = parsed.token || parsed.access_token || parsed.accessToken || parsed;
            return !!token;
          }
        } catch (e) {
          // not JSON, use raw value
          token = stored;
          return true;
        }
        return false;
      });

      // Also try `user` object which may contain access_token
      if (!token) {
        const userRaw = localStorage.getItem('user');
        if (userRaw) {
          try {
            const userObj = JSON.parse(userRaw);
            token = userObj?.access_token || userObj?.accessToken || userObj?.token || token;
          } catch (e) {
            // ignore
          }
        }
      }
    }

    // Add Authorization header if token exists
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let pendingRequests = [];

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // CSRF token missing/invalid: fetch fresh token and retry once
    if ((error.response?.status === 403 || error.response?.status === 419) && !originalRequest._csrfRetry) {
      originalRequest._csrfRetry = true;
      try {
        const { data } = await api.get('/csrf-token');
        if (data?.csrfToken) {
          api.defaults.headers.common['X-CSRF-Token'] = data.csrfToken;
          originalRequest.headers['X-CSRF-Token'] = data.csrfToken;
        }
        return api(originalRequest);
      } catch (e) {
        // fall through to 401 handling or reject
      }
    }

    // 401 Unauthorized: attempt refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          // Ensure CSRF token before refresh
          try {
            const { data } = await api.get('/csrf-token');
            if (data?.csrfToken) {
              api.defaults.headers.common['X-CSRF-Token'] = data.csrfToken;
            }
          } catch (_) {
            // Ignore CSRF token fetch errors during refresh
          }

          await api.post('/admin/refresh'); // adjust refresh endpoint if needed
          isRefreshing = false;
          pendingRequests.forEach((cb) => cb());
          pendingRequests = [];
          return api(originalRequest);
        } catch (refreshErr) {
          isRefreshing = false;
          pendingRequests = [];
          if (typeof window !== 'undefined') {
            window.location.replace('/login');
          }
          return Promise.reject(refreshErr);
        }
      }

      return new Promise((resolve) => {
        pendingRequests.push(() => resolve(api(originalRequest)));
      });
    } 

    // 403 Forbidden: show toast warning
    if (error.response?.status === 403) {
      toast.warning("You don't have permission to access this resource.");
    }

    return Promise.reject(error);
  }
);

export default api;
